import { Easing, Group, Tween } from "@tweenjs/tween.js";
import * as PIXI from "pixi.js";

export const createDiceRollSlideShow = (
  textures: PIXI.Texture[],
  finalTextureIndex: number,
  totalRollAnimTime: number,
  startDelay: number = 0,
  scaleStart: number,
  playAreaOrigin: { x: number; y: number },
  positionStart: { x: number; y: number },
  scaleEnd?: number,
  positionEnd?: { x: number; y: number },
  positionDoubles?: { x: number; y: number }
): { sprite: PIXI.AnimatedSprite; group: Group; duration: number } => {
  const shuffledTextures = [...textures].sort(() => Math.random() - 0.5);
  const totalFrames = startDelay === 0 ? 12 : 1;
  const initialDuration = 20;
  const frames: { texture: PIXI.Texture; time: number }[] = [];
  let totalDuration = startDelay;
  for (let i = 0; i < totalFrames; i++) {
    const lastFrame = i === totalFrames - 1;
    const texture = lastFrame
      ? textures[finalTextureIndex % shuffledTextures.length]
      : shuffledTextures[i % shuffledTextures.length];
    const time =
      lastFrame && totalFrames > 1 ? 500 : initialDuration + Math.floor(Math.pow(1.25, i) * 30);
    totalDuration += time;
    frames.push({ texture, time });
  }
  const duration = totalDuration;

  const diceSlides = new PIXI.AnimatedSprite(frames);
  diceSlides.anchor.set(0.5, 0.5);
  diceSlides.position.set(positionStart.x, positionStart.y);
  diceSlides.scale.set(scaleStart, scaleStart);
  diceSlides.roundPixels = true;
  diceSlides.alpha = 0;
  diceSlides.loop = false;
  diceSlides.play();

  const group = new Group();
  const alphaTween = new Tween({ alpha: 0 })
    .to({ alpha: 1 }, 500)
    .delay(startDelay)
    .onUpdate((values: { alpha: number }) => {
      diceSlides.alpha = values.alpha;
    });
  group.add(alphaTween);

  let moveStartX = positionStart.x - playAreaOrigin.x;
  let moveStartY = positionStart.y - playAreaOrigin.y;

  if (positionDoubles) {
    moveStartX = positionDoubles.x - playAreaOrigin.x;
    moveStartY = positionDoubles.y - playAreaOrigin.y;
    const doublesStartY = positionStart.y - playAreaOrigin.y;
    const doublesTween = new Tween({ value: 0 })
      .to({ value: 1 }, 500)
      .easing(Easing.Quadratic.InOut)
      .delay(totalDuration)
      .onUpdate((current) => {
        diceSlides.y =
          doublesStartY + (positionDoubles.y - playAreaOrigin.y - doublesStartY) * current.value;
      });

    totalDuration += doublesTween.getDuration();
    group.add(doublesTween);
  }

  if (positionEnd) {
    const moveTween = new Tween({ value: 0 })
      .to({ value: 1 }, totalRollAnimTime - totalDuration)
      .easing(Easing.Quadratic.InOut)
      .delay(totalDuration)
      .onUpdate((current) => {
        diceSlides.position.set(
          moveStartX + (positionEnd.x - playAreaOrigin.x - moveStartX) * current.value,
          moveStartY + (positionEnd.y - playAreaOrigin.y - moveStartY) * current.value
        );
      });
    group.add(moveTween);
  }

  if (scaleEnd) {
    const scaleTween = new Tween({ scaleX: scaleStart, scaleY: scaleStart })
      .to({ scaleX: scaleEnd, scaleY: scaleEnd }, totalRollAnimTime - totalDuration)
      .delay(totalDuration)
      .onUpdate((values: { scaleX: number; scaleY: number }) => {
        diceSlides.scale.set(values.scaleX, values.scaleY);
      });
    group.add(scaleTween);
  }

  return { sprite: diceSlides, group: group, duration: duration };
};
